import { Input, Label } from '@slal/ui/forms';
import { Col, Row } from '@slal/ui/layout';
import { useController } from '@slal/ui/react-hook-form/core';
import { TextFieldProps } from '@slal/ui/react-hook-form/fields';
import { padStart } from 'lodash';
import { useCallback } from 'react';
import { normalizePaddedNumbersRange } from '~/utils';
import { StyledDateFieldSet, StyledInput } from './styles';

export const DateField = ({ name, ...otherProps }: TextFieldProps) => {
    const {
        field: { onChange, ref, ...field },
    } = useController({ name, defaultValue: '' });

    const dayName = `${name}-day`;
    const monthName = `${name}-month`;
    const yearName = `${name}-year`;
    const parts = field.value ? field.value.split('-') : ['', '', ''];

    const updateDay: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 31);
            const nextValue = setValueAs(value, parts[2]);
            onChange(`${parts[0] || ''}-${parts[1] || ''}-${nextValue}`);
        },
        [parts, onChange]
    );

    const updateMonth: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 12);
            const nextValue = setValueAs(value, parts[1]);
            onChange(`${parts[0] || ''}-${nextValue}-${parts[2] || ''}`);
        },
        [parts, onChange]
    );

    const updateYear: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        event => {
            const value = event.target?.value || '';
            const setValueAs = normalizePaddedNumbersRange(0, 2050);
            const nextValue = setValueAs(value, parts[0]);
            onChange(`${nextValue}-${parts[1] || ''}-${parts[2] || ''}`);
        },
        [parts, onChange]
    );

    const updateValue = useCallback(() => {
        const day = parts[2] && parts[2] !== '0' ? padStart(parts[2], 2, '0') : parts[2] || '';
        const month = parts[1] && parts[1] !== '0' ? padStart(parts[1], 2, '0') : parts[1] || '';
        const year = parts[0] && parts[0] !== '0' ? padStart(parts[0], 4, '0') : parts[0] || '';
        onChange(`${year}-${month}-${day}`);
    }, [parts, onChange, ref]);

    return (
        <StyledDateFieldSet>
            <Input type="hidden" id={name} {...field} {...otherProps} />

            <Row gutters={false}>
                <Col col="auto">
                    <Label htmlFor={dayName}>Day</Label>
                    <StyledInput
                        aria-label="Day"
                        value={parts[2] || ''}
                        id={dayName}
                        name={dayName}
                        maxLength={2}
                        onChange={updateDay}
                        onBlur={updateValue}
                        placeholder="DD"
                        size={2}
                    />
                </Col>

                <Col col="auto">
                    <Label htmlFor={monthName}>Month</Label>
                    <StyledInput
                        aria-label="Month"
                        value={parts[1] || ''}
                        id={monthName}
                        name={monthName}
                        maxLength={2}
                        onChange={updateMonth}
                        onBlur={updateValue}
                        placeholder="MM"
                        size={2}
                    />
                </Col>

                <Col col="auto">
                    <Label htmlFor={yearName}>Year</Label>
                    <StyledInput
                        aria-label="Year"
                        value={parts[0] || ''}
                        id={yearName}
                        name={yearName}
                        maxLength={4}
                        onChange={updateYear}
                        onBlur={updateValue}
                        placeholder="YYYY"
                        size={4}
                    />
                </Col>
            </Row>
        </StyledDateFieldSet>
    );
};
